<script setup lang="ts">
import { BrComponent, BrPage } from '@bloomreach/vue-sdk'
import { type Ref, inject, provide, ref, useTemplateRef } from 'vue'
import { useBrxmData } from '../../composables/useBrxmData'
import { useBrxm } from '../../composables/useBrxm'
import { MAIN_PAGE } from '../../consts/injectProvideKeys'
import type { ITabApi } from './types'
import useBreakpoint from '@/ui/composables/useBreakpoint'
import type { IBrxmProps } from '@/cms-ui/ts/interfaces/IBrxmProps'
import { EVariant, type ITabItem } from '@/ui/plushComponents/VTabs/types'
import { useVisibilityTracking } from '@/cms-ui/composables/useAnalytics'

const props = defineProps<IBrxmProps>()

const injectedPage = inject(MAIN_PAGE)
if (!injectedPage) {
    provide(MAIN_PAGE, props.page)
}
const data = useBrxmData(props, 'TABS')
const { mapping, configuration } = await useBrxm()
const { isMobile } = useBreakpoint()
const tabsRef = useTemplateRef('tabsRef')

const active: Ref<number> = ref(0)
const tabsList = data?.value?.tabs?.map((tabItem: ITabApi, index: number) => {
    if (tabItem.selected) {
        active.value = index
    }

    return {
        name: tabItem.title,
        icon: tabItem.icon,
        value: index,
        content: tabItem.components,
    }
})

useVisibilityTracking(tabsRef, 'Tabs', 'Tabs')
</script>

<template>
    <section
        ref="tabsRef"
        class="space-standard container"
    >
        <VTabs
            :tabs-list="tabsList"
            :default-value="active"
            list-aria-label="list aria label"
            :data-test="`tabs_${data?.id}`"
            size="LARGE"
            :dark="data?.darkmode"
            :layout="isMobile ? data?.mobileLayout?.toLowerCase() : data?.desktopLayout?.toLowerCase()"
            :variant="data?.visualVariant?.toLowerCase() === 'pills' ? EVariant.PILL : EVariant.DEFAULT"
        >
            <template #default="{ activeSlide }">
                <VTabsAnimation>
                    <template v-if="tabsList?.length">
                        <VTabsContent
                            v-for="tab in tabsList?.filter((tab: ITabItem) => activeSlide === tab.value)"
                            :key="tab.value"
                            :value="tab.value"
                            class="tab-content"
                        >
                            <template v-if="tab.content">
                                <template
                                    v-for="(child, index) in tab.content"
                                    :key="`component_${index}`"
                                >
                                    <BrPage
                                        :configuration="configuration"
                                        :mapping="mapping"
                                        :page="child"
                                    >
                                        <template #default="{ component }">
                                            <BrComponent :component="component" />
                                        </template>
                                    </BrPage>
                                </template>
                            </template>
                        </VTabsContent>
                    </template>
                </VTabsAnimation>
            </template>
        </VTabs>
    </section>
</template>

<style>
.tab-content .container {
  padding-left: 0px !important;
  padding-right: 0px !important;
}
</style>
